import { Component, Input, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { CountriesService } from '@core/services/countries.service';
import { AllowedLocation } from '@core/typings/program.typing';
import { TypeSafeFormGroup, TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { SpecialHandlingFormGroup } from '../special-handling-modal/special-handling-modal.component';

export interface AddressFormGroup {
  name?: string;
  address1: string;
  address2: string;
  countryCode: string;
  city: string;
  stateProvRegCode: string;
  postalCode: string;
}

@Component({
    selector: 'gc-address-block',
    templateUrl: './address-block.component.html',
    styleUrls: ['./address-block.component.scss'],
    standalone: false
})
export class AddressBlockComponent implements OnInit {
  @Input() addressFormGroup: TypeSafeFormGroup<AddressFormGroup>|TypeSafeFormGroup<SpecialHandlingFormGroup>;
  @Input() countryDisabled = false;
  @Input() disableInputs = false;
  @Input() enforceCustomStateValidation = true;
  @Input() allowedOrgLocations: AllowedLocation[];

  countrySelects = this.countriesService.countrySelects;
  countryToRegionOptionsMap = this.countriesService.countryCodeToRegionOptionsMap;
  regionSelects: TypeaheadSelectOption[] = [];
  regionsExist: boolean;

  constructor (
    private countriesService: CountriesService
  ) { }

  ngOnInit () {
    this.setRegionsOnCountryChange();
  }

  setRegionsOnCountryChange () {
    const countryCode = this.addressFormGroup.value.countryCode;
    const stateControl = (this.addressFormGroup as TypeSafeFormGroup<AddressFormGroup>).get('stateProvRegCode');
    const currentStateValue = stateControl.value;

    if (!!countryCode) {
      let regionSelects = this.countryToRegionOptionsMap[countryCode] ?? [];
      if (this.allowedOrgLocations?.length > 0) {
        // Filter country options when enforceCustomStateValidation is true
        if (this.enforceCustomStateValidation) {
          const allowedCountryCodes = this.allowedOrgLocations.map(location =>
            this.countriesService.countryIdToCountryMap[location.countryId].countryCode
          );
          this.countrySelects = this.countriesService.countrySelects.filter(country =>
            allowedCountryCodes.includes(country.value)
          );
        }

        const foundCountry = this.allowedOrgLocations.find((location) => {
          const _countryCode = this.countriesService.countryIdToCountryMap[location.countryId].countryCode;

          return _countryCode === countryCode;
        });
        if (!!foundCountry) {
          if (foundCountry.regionIds.length > 0) {
            regionSelects = (this.countriesService.countryIdToRegionOptionsMap[foundCountry.countryId] ?? [])
              .filter(option => foundCountry.regionIds.includes(option.value as number))
              .map(option => ({
                label: option.label,
                value: this.countriesService.regionIdToRegionMap[option.value as number]?.regionCode
              }));
          }
        }
      }
      this.setRegionSelects(regionSelects);

      // Clear stateProvRegCode if current value is not in the updated regionSelects
      if (currentStateValue && this.regionSelects.length > 0) {
        const isCurrentStateValid = this.regionSelects.some(region => region.value === currentStateValue);
        if (!isCurrentStateValid) {
          stateControl.setValue(null);
        }
      }
    } else {
      this.regionSelects = [];
      this.regionsExist = false;
      // Clear state when no country is selected
      if (currentStateValue) {
        stateControl.setValue(null);
      }
    }

    if (this.enforceCustomStateValidation) {
      if (this.regionSelects.length === 0) {
        stateControl.clearValidators();
      } else if (!stateControl.hasValidator(Validators.required)) {
        stateControl.addValidators(Validators.required);
      }
    }

    stateControl.updateValueAndValidity();
  }

  setRegionSelects (regionSelects: TypeaheadSelectOption[]) {
    this.regionSelects = regionSelects;
    this.regionsExist = this.regionSelects.length > 0;
  }
}
